<script lang="ts">
	import classNames from 'classnames';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { page } from '$app/state';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { HeniArtistsLogo } from '$global/assets/logos/HeniArtistsLogo';
	import { Button } from '$global/components/Button';
	import {
		GlobalCard,
		type GlobalCardProps,
	} from '$global/components/GlobalCard';
	import { Txt } from '$global/components/Txt';
	import { extractNonNull } from '$global/utils/extractNonNull/extractNonNull';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import type { GlobalHomePageData } from '$routes/types';

	const dataCy = 'home-artists';

	const featuredArtists: GlobalHomePageData['featuredArtists'] =
		page.data.featuredArtists;
	const artistsIntro = page.data.globalContent['artists_intro'];

	const cards = featuredArtists.reduce(
		(
			accumulator: Omit<GlobalCardProps, 'dataCy'>[],
			featuredArtist: GlobalHomePageData['featuredArtists'][number]
		) => {
			if (!featuredArtist?.artist) {
				return accumulator;
			}

			const artist = extractNonNull(
				featuredArtist.artist,
				['bio_image', 'name', 'slug'],
				{}
			);

			if (!artist) {
				return accumulator;
			}

			const artistImage = extractNonNull(
				artist.bio_image,
				['filename_disk', 'width', 'height'],
				{}
			);

			if (!artistImage) {
				return accumulator;
			}

			return [
				...accumulator,
				{
					line1: artist.name,
					href: `${GlobalRoutes.ArtistsHome}/${artist.slug}`,
					image: {
						url: `${getImageUrl(artistImage.filename_disk)}`,
						width: artistImage.width,
						height: artistImage.height,
					},
					alt: artist.name,
					squared: true,
				},
			];
		},
		[] as Omit<GlobalCardProps, 'dataCy'>[]
	);
</script>

<section>
	<div class="mb-6 flex items-start justify-between">
		<div>
			<HeniArtistsLogo class="mb-2 w-auto" />
			<Txt variant="body2" class={classNames('mb-3')}>
				{artistsIntro}
			</Txt>
		</div>

		<a
			data-cy={`${dataCy}-view-all-artists-top`}
			href={GlobalRoutes.ArtistsHome}
			class="hidden md:block"
		>
			<Button
				dataCy={`${dataCy}-view-all-artists-top`}
				variant="secondary"
				size="md"
			>
				View all

				{#snippet trailing()}
					<ChevronRightIcon />
				{/snippet}
			</Button>
		</a>
	</div>

	<div class="mb-6 grid w-full grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-5">
		{#each cards as card, i}
			<GlobalCard
				{dataCy}
				{...card}
				mobileVertical
				classes={{
					background: 'aspect-square !h-auto !pb-0',
					title: 'px-1 py-3',
				}}
				class={classNames({ 'hidden lg:flex': i === 4 })}
			/>
		{/each}
	</div>

	<div class="sm:flex sm:justify-end md:hidden">
		<a
			data-cy={`${dataCy}-view-all-artists-bottom`}
			href={GlobalRoutes.ArtistsHome}
		>
			<Button
				dataCy={`${dataCy}-view-all-artists-bottom`}
				variant="secondary"
				size="md"
				class="w-full sm:w-auto"
			>
				View all artists

				{#snippet trailing()}
					<ChevronRightIcon />
				{/snippet}
			</Button>
		</a>
	</div>
</section>
